/**
 * Store for the company form
 */
import { v4 as uuidv4 } from 'uuid';
import {persistCompany, defaultCompanyState, defaultCompanyRules} from "api/companyform"
import ContactApi from 'api/contacts';
import { debounce } from 'lodash';
import { persistCustomFields } from 'api/third';
import Third from 'stores/forms/third';
import {
    addressState,
    addressGetters,
    addressMutations,
    addressActions,
    persistAddresses,
} from "./addressHelpers";

export default {
    namespaced: true,
    state() {
        return {
            ...Third.state(),
            loading : false,
            companyForm: defaultCompanyState(),
            companyRules:  defaultCompanyRules(),
            addressSection: addressState(),
            sideParams: {
                rateCategories: true,
                note: true,
                social: true,
                smarttags: true,
                syncProviders : false,
            },
        }
    },
    getters: {
        ...Third.getters,
        ...addressGetters,
        getModel: (state, getters)=>{
            let model = {
                ...state.companyForm.companySettings,
                ...state.companyForm.paramsSettings,
                ...state.companyForm.legalSettings,
                ...state.companyForm.accounting,
                addresses : {...state.addressSection.addresses},
                contacts : {...state.companyForm.contacts},
                groupedCustomFields: getters.groupedCustomFields
            }
            return model;
        },
        getState: (state, getters) => {
            return {
                companySettings : {...state.companyForm.companySettings},
                paramsSettings : {...state.companyForm.paramsSettings},
                legalSettings : {...state.companyForm.legalSettings},
                accounting : {...state.companyForm.accounting},
                addressesFieldsStatus : state.addressSection.addressesFieldsStatus,
                addresses : [...state.addressSection.addresses],
                contacts : [...state.companyForm.contacts],
                contactsList: [...state.companyForm.listings.contactsList],
                customFields: [...getters.customFields]
            }
        },
        getRules: (state)=>{
            return state.companyRules
        },
        getLoading: state => {
            return state.loading;
        },
        /**
         * Get main data in form
         */
        getId: (state) => {
            return state.companyForm.id;
        },
        getName: (state) => {
            return state.companyForm.companySettings.name;
        },
        getType: (state) => {
            return state.companyForm.companySettings.type;
        },
        getEmail: (state) => {
            return state.companyForm.companySettings.email;
        },
        getWebsite: (state) => {
            return state.companyForm.companySettings.website;
        },
        getRef: (state) => {
            return state.companyForm.companySettings.reference;
        },
        getPhone: (state) =>{
            return state.companyForm.companySettings.contactNumber.phone;
        },
        getMobile: (state) =>{
            return state.companyForm.companySettings.contactNumber.mobile;
        },
        getFax: (state) =>{
            return state.companyForm.companySettings.contactNumber.fax;
        },
        getBusinessSegments: (state) => {
            return state.companyForm.companySettings.businessSegments;
        },
        getNumberOfEmployees: (state) => {
            return state.companyForm.companySettings.numberOfEmployees;
        },
        /**
         * Get Legal and billing settings data in form
         */
        getCapital: (state) =>{
            return state.companyForm.legalSettings.capital;
        },
        getLegalFrance: (state) =>{
            return state.companyForm.legalSettings.legalFrance;
        },
         /**
         * Get contacts related to the company
         */
        getContacts: (state) =>{
            return state.companyForm.contacts;
        },
        getContact: (state) => (index)=>{
            return state.companyForm.contacts[index];
        },

        /**
         * Get params data in form (in side panel)
         */
        getParamsConfig: (state, getters) => {
            return {
                ...state.sideParams,
                rateCategories: getters.getType !== 'supplier'
            }
        },
        getSocial: (state) =>{
            return state.companyForm.paramsSettings.social;
        },
        getNote: (state) =>{
            return state.companyForm.paramsSettings.note;
        },
        getFacebook: (state) => {
            return state.companyForm.paramsSettings.social.facebook
        },
        getTwitter: (state) => {
            return state.companyForm.paramsSettings.social.twitter
        },
        getLinkedin: (state) => {
            return state.companyForm.paramsSettings.social.linkedin
        },
        getRateCategories: (state) =>{
            return state.companyForm.paramsSettings.rateCategories;
        },
        smartTags: state => {
            let tags = [];
            if(state.companyForm.paramsSettings.smartTags.length > 0){
                tags = state.companyForm.paramsSettings.smartTags.map(tag => {
                    let id = (!_.isNil(tag.id) ? parseInt(tag.id) : null)
                    return {...tag, id: id }
                });
            }
            return tags
        },
        getSmartTagsType: state => {
            return state.companyForm.smartTagsType;
        },
        getSyncProviders: (state) =>{
            return state.companyForm.paramsSettings.syncProviders;
        },
        /**
         * Get accounting data in form
         */
        getAuxiliaryCode: (state) =>{
            return state.companyForm.accounting.auxiliaryCode;
        },
        getAccountingCodeId: (state) =>{
            return state.companyForm.accounting.accountingCodeId;
        },
        getAccountingPurchaseCodeId: (state) =>{
            return state.companyForm.accounting.accountingPurchaseCodeId;
        },

        /**
         * Get metadatas / list
         */
        getSearchAvailable: (state) => {
            return state.companyForm.modulesSearch;
        },
        getTypes: (state) => {
            return state.companyForm.listings.types;
        },
        getBusinessSegmentsList: (state) => {
            return state.companyForm.listings.businessSegmentsList;
        },
        getEmployeesNumberList: (state) => {
            return state.companyForm.listings.employeesNumber;
        },
        getAccountingCodes: (state) => {
            return state.companyForm.listings.accountingCodes;
        },
        getAccountingPurchaseCodes: (state) => {
            return state.companyForm.listings.accountingPurchaseCodes;
        },
        getRateCategoriesList: (state) => {
            return state.companyForm.listings.rateCategories;
        },
        getSyncProvidersList: (state) => {
            return state.companyForm.listings.syncProviders;
        },
        getAccountingEnabled: (state) => {
            return state.companyForm.accounting.accountingEnabled;
        },
        getUseThirdAux: (state) => {
            return state.companyForm.accounting.useThirdAux;
        },
        getSuggestionContactsList: (state) =>{
            return state.companyForm.listings.contactsList;
        },
        getSuggestionCompaniesList: (state) =>{
            return state.companyForm.listings.companiesList;
        },
    },
    mutations: {
        ...Third.mutations,
        ...addressMutations,
        setLoading: (state, payload) => {
            state.loading = payload;
        },
        setComputeLoading(state, payload) {
            state.companyForm.computeLoading = payload;
        },
        /**
         * update main data in form
         */
        setState: (state, payload) =>{
            state.companyForm.companySettings = payload.companySettings;
            state.companyForm.legalSettings = payload.legalSettings;
            state.companyForm.paramsSettings = payload.paramsSettings;
            state.companyForm.accounting = payload.accounting;
            state.addressSection.addresses = payload.addresses;
            state.companyForm.contacts = payload.contacts;
            state.addressSection.addressesFieldsStatus = payload.addressesFieldsStatus
            state.companyForm.listings.contactsList = payload.contactsList
            state.companyForm.listings.companiesList = payload.companiesList
        },
        setId: (state, payload) => {
            state.companyForm.id = payload;
        },
        setForm: (state, payload) =>{
            state.companyForm = payload;
        },
        setName: (state, payload) => {
            state.companyForm.companySettings.name = payload;
        },
        setType: (state, payload) => {
            if(payload === 'supplier'){
                state.companyForm.paramsSettings.rateCategories = {value : null}
            }else if(state.companyForm.paramsSettings.rateCategories && _.isNil(state.companyForm.paramsSettings.rateCategories.value)){
                let defaultRateCategories = state.companyForm.listings.rateCategories.find(rate => !!rate.is_default);
                if(_.isNil(defaultRateCategories)){
                    defaultRateCategories = state.companyForm.listings.rateCategories[0]
                }
                state.companyForm.paramsSettings.rateCategories = defaultRateCategories
            }
            state.companyForm.companySettings.type = payload;
        },
        setEmail: (state, payload) => {
            state.companyForm.companySettings.email = payload;
        },
        setWebsite: (state, payload) => {
            state.companyForm.companySettings.website = payload;
        },
        setRef: (state, payload) => {
            state.companyForm.companySettings.reference = payload;
        },
        setBusinessSegments: (state, payload) => {
            state.companyForm.companySettings.businessSegments = payload;
        },
        setPhone: (state, payload) => {
            state.companyForm.companySettings.contactNumber.phone = payload;
        },
        setMobile: (state, payload) => {
            state.companyForm.companySettings.contactNumber.mobile = payload;
        },
        setFax: (state, payload) => {
            state.companyForm.companySettings.contactNumber.fax = payload;
        },
        setNumberOfEmployees: (state, payload) => {
            state.companyForm.companySettings.numberOfEmployees = payload;
        },
        /**
         * update Legal and billing settings data in form
         */
        setCapital: (state, payload) =>{
            state.companyForm.legalSettings.capital = payload;
        },
        setLegalFrance: (state, payload) =>{
            state.companyForm.legalSettings.legalFrance = payload;
        },
        /**
         * update params data in form (in side panel)
         */
        setSocial: (state, payload) =>{
            state.companyForm.paramsSettings.social = payload;
        },
        setFacebook(state, facebookURL) {
            state.companyForm.paramsSettings.social.facebook = facebookURL;
        },
        setTwitter(state, twitterURL) {
            state.companyForm.paramsSettings.social.twitter = twitterURL;
        },
        setLinkedin(state, linkedinURL) {
            state.companyForm.paramsSettings.social.linkedin = linkedinURL;
        },
        setNote: (state, payload) =>{
            state.companyForm.paramsSettings.note = payload;
        },
        setRateCategories: (state, payload) =>{
            state.companyForm.paramsSettings.rateCategories = payload;
        },
        setSmartTags(state, tags) {
            state.companyForm.paramsSettings.smartTags = tags;
        },

        setContact: (state, payload)=>{
            state.companyForm.contacts[payload.index] = payload.datas;
        },
        setSyncProviders: (state, payload) =>{
            state.companyForm.paramsSettings.syncProviders = payload;
        },

        /**
         * update accounting data in form
         */
        setAuxiliaryCode: (state, payload) =>{
            state.companyForm.accounting.auxiliaryCode = payload
        },
        setAccountingCodeId: (state,payload) =>{
            state.companyForm.accounting.accountingCodeId = payload;
        },
        setAccountingPurchaseCodeId: (state,payload) =>{
            state.companyForm.accounting.accountingPurchaseCodeId = payload;
        },

        /**
         * Set metadatas from fetch / list
         */
        setLists: (state, payload)=>{
            state.companyForm.listings = payload;
        },
        setTypes: (state, payload) => {
            if(payload === "supplier"){
                state.companyForm.companySettings.businessSegments = {label:  window.i18n.__("Aucun"), value: null};
            }
            state.companyForm.listings.types = payload;
        },
        setSearchAvailable: (state, payload)=>{
            state.companyForm.modulesSearch = payload
        },
        setBusinessSegmentsList: (state, payload) => {
            state.companyForm.listings.businessSegmentsList = payload;
        },
        setEmployeesNumberList: (state, payload) => {
            state.companyForm.listings.employeesNumber = payload;
        },
        setAccountingCodes: (state, payload) => {
            state.companyForm.listings.accountingCodes = payload;
        },
        setAccountingPurchaseCodes: (state, payload) => {
            state.companyForm.listings.accountingPurchaseCodes = payload;
        },
        setRateCategoriesList: (state, payload) => {
            state.companyForm.listings.rateCategories = payload;
        },
        setSyncProvidersList: (state, payload) => {
            state.companyForm.listings.syncProviders = payload;
        },
        setAccountingEnabled: (state, payload) => {
            state.companyForm.accounting.accountingEnabled = payload;
        },
        setUseThirdAux: (state, payload) => {
            state.companyForm.accounting.useThirdAux = payload;
        },
        setSuggestionContactsList: (state, payload) =>{
            state.companyForm.listings.contactsList = payload;
        },
        setSuggestionCompaniesList: (state, payload) =>{
            state.companyForm.listings.companiesList = payload;
        },

        setSearchCompanyDatas: (state, payload) => {
            state.companyForm.companySettings.name = payload.name;
            state.companyForm.companySettings.website = payload.website ? payload.website : "";
        }
    },
    actions :{
        ...Third.actions,
        ...addressActions,
       /**
         * Create or update third datas
         * @param {*}
         * @returns
         */
        updateThird: ({state, commit, dispatch, getters}, action) => {
            commit("setAddressesFieldsStatus", "saved");
            const uuid = uuidv4();
            let smartTags = getters.smartTags ? getters.smartTags : {};

            //persist company datas
            const datas = [
                {"uri": "/companies", "method": "POST", "uuid": uuid},
                persistCompany(state.companyForm),
                { "uri": `/companies/${uuid}/smart-tags`, "method": "POST"},
                smartTags
            ];

            //persist company addresses
            if (
                state.addressSection.addresses &&
                state.addressSection.addresses.length > 0
            ) {
                dispatch("formatAddressesBeforeSubmit");
                state.addressSection.addresses.forEach((address) => {
                    if (!address.active) {
                        datas.push({"uri": `/companies/${uuid}/addresses`, "method": "POST"});
                        datas.push(persistAddresses(address));
                    }
                });
            }

            //persist contacts datas
            if(state.companyForm.contacts && state.companyForm.contacts.length > 0){
                state.companyForm.contacts.forEach((contact)=>{
                    let contactUuid = uuidv4();
                    let smartags = (contact.contactSettings.smartTags && contact.contactSettings.smartTags.length > 0) ? contact.contactSettings.smartTags : {};
                    datas.push({"uri": `/contacts`, "method": "POST", "uuid": contactUuid})
                    datas.push(ContactApi.persistContact(contact.contactSettings))
                    datas.push({"uri": `/contacts/${contactUuid}/smart-tags`, "method": "POST"})
                    datas.push(smartags)
                    if(contact.contactSettings.customFields.length) {
                        datas.push({ "uri": `/contacts/${contactUuid}/custom-fields`, "method": "PUT" }),
                        datas.push(persistCustomFields(contact.contactSettings.customFields));
                    }
                })
            }

            //persist custom fields datas
            if(getters.customFields.length) {
                datas.push({ "uri": `/companies/${uuid}/custom-fields`, "method": "PUT" }),
                datas.push(persistCustomFields(getters.customFields));
            }

            let ndjson = datas.map(JSON.stringify).join('\n')
            return new Promise((resolve, reject) =>{
                ApiBatchClient.post("/batch", {}, ndjson).then((res)=>{
                    if(!res.data.error){
                        commit("setId", res.data.responses[0].response.id);
                        if(state.companyForm.contacts && state.companyForm.contacts.length > 0){
                            //find contact response
                            let contactIndex = res.data.responses.filter((data) => data.response.last_name)
                            let contactsId = []
                            contactIndex.forEach((contact)=>{contactsId.push(contact.response.id)})
                            let linkContactDatas = {companyId : res.data.responses[0].response.id, ids : contactsId}
                            dispatch("linkContactsToThird", linkContactDatas).then((contactRes)=>{
                                resolve()
                            }).catch((contactErr)=>{
                                reject(contactErr.data.responses.find(response => response.response.code >= 400));
                            })
                        }
                        if(action && action !== ""){
                            dispatch("clearThird")
                        }
                        Push.success(window.i18n.__('Société ajoutée avec succès'));
                        resolve({id: res.data.responses[0].response.id, type: res.data.responses[0].response.type})
                    }else{
                        commit("setAddressesFieldsStatus", "new");
                        reject(res.data.responses.find(response => response.code >= 400));
                    }
                }).catch(err =>{
                    reject(err);
                })
            })
        },

        updateSyncProviders:({commit}, syncs) =>{
            commit("setSyncProviders", syncs)
        },
        updateCompaniesList({dispatch, commit}, search){
            return new Promise((resolve ,reject)=>{
                let list = [];
                dispatch("fetchCompaniesTypeSearch",{q: search, type: "prospect"}).then((res)=>{
                    list = (!_.isUndefined(res) ? res : list);
                    dispatch("fetchCompaniesTypeSearch",{q: search, type: "client"}).then((res)=>{
                        list = (!_.isUndefined(res) ? list.concat(res) : list);
                        commit("setSuggestionCompaniesList", list)
                        resolve();
                    }).catch((err)=>{
                        reject();
                    })
                }).catch((err)=>{
                    reject();
                })
            })
        },

        /**
         * Link all contact added to that new company
         * @param {*} param0
         * @param {*} apiRes : responde from previous api call
         * @returns
         */
        linkContactsToThird({}, apiRes){
            let datas = [];
            for(let index = 0; index < apiRes.ids.length ; index++){
                datas.push({"uri": `/companies/${apiRes.companyId}/contacts/${apiRes.ids[index]}`, "method": "POST"})
                datas.push({})
            }
            let ndjson = datas.map(JSON.stringify).join('\n')
            return new Promise((resolve, reject)=> {
                ApiBatchClient.post("/batch", {}, ndjson).then((res)=>{
                    resolve(res)
                })
                .catch((err)=>{
                    reject(err)
                })
            })
        },
        /**
         * Reset current form
         * @param {}
         */
        clearThird: ({state, commit}) => {
            let lists = state.companyForm.listings;
            lists.contactsList = [];
            lists.companiesList = []

            let searchAvailable = state.companyForm.modulesSearch;
            commit('setForm', defaultCompanyState())
            commit('setLists', lists)
            commit("resetCustomFields")
            commit("clearAddresses")
            commit("setSearchAvailable", searchAvailable)
            commit('setSmartTags', []);
            commit('setType', '')
            window.EventBus.$emit('form:third:create:setAddressAlgolia', '');

        },
        removeContact: ({state}, index) =>{
            const contact = state.companyForm.contacts.splice(index, 1);
            if(contact[0].contactSettings.isLinkedContact){
                const contactToAdd = {
                    forename: contact[0].contactSettings.forename,
                    name: contact[0].contactSettings.name,
                    role: contact[0].contactSettings.role,
                    id: null
                }
                state.companyForm.listings.contactsList.push(contactToAdd)
            }
        },

        fetchMetadata: ({dispatch}) =>{
            return new Promise((resolve, reject) => {
                ApiClient.get("/companies/metadata", {}).then((res) => {
                    dispatch('commitMetaData', res);
                    dispatch("createNewAddress");
                    resolve(res);
                }, err => {
                    reject(err);
                })
            })
        },

        /**
         * Recover datas of the pending form
         * @param {} store : recover saved store
         */
        fetchPendingStore: ({getters, commit, state}, store) =>{
            let contacts = store.state.contacts;
            //if new data is new contact
            if(store.new && store.newDatas?.datas?.contact){
                contacts.push({
                    contactSettings : store.newDatas.datas.contact,
                    id : store.newDatas.datas.id
                })
                store.state.contacts = contacts

                //if from suggestion contact list
                if(!_.isNil(store.index) && Number.isInteger(store.index)){
                    if(store.state.contactsList.length !== (store.index + 1) ){
                        store.state.contactsList.splice(store.index, 1)
                    }else{
                        store.state.contactsList.pop();
                    }
                }
            }
            commit("setState", store.state)
            let addresses = getters.getAddresses

            //if last address wasn't save
            if(getters.getAddressesFieldsStatus === "pending" && addresses.length > 1 && addresses[addresses.length - 1].active){
                state.companyForm.addresses.pop()
            }

            //If edit contact
            if(!store.new && !_.isNil(store.index) && store.newDatas?.datas?.contact){
                commit("setContact", {
                    index: store.index,
                    datas: {
                        contactSettings : store.newDatas.datas.contact,
                        id : store.newDatas.datas.id
                    }
                })
            }
        },

        commitMetaData: ({commit}, res) =>{
            //add "none" as an option and set default business segment as none
            const none = [{label:  window.i18n.__("Aucun"), value: null}]
            commit("setBusinessSegments", none[0]);

            //search is available
            commit("setSearchAvailable", res.data.firmsearch)

            commit('setTypes', res.data.types)
            commit('setBusinessSegmentsList', none.concat(res.data.business_segments))
            commit('setEmployeesNumberList', res.data.number_of_employees)
            commit('setAccountingCodes', res.data.accounting.accounting_codes)
            commit('setAccountingPurchaseCodes', res.data.accounting.accounting_purchase_codes)
            commit('setRateCategoriesList', res.data.rate_categories)

            //set the object with is_default as true as the default value in selector
            let defaultRateCategories = res.data.rate_categories.find(rate => !!rate.is_default);
            if(_.isNil(defaultRateCategories)){
                defaultRateCategories = res.data.rate_categories[0]
            }
            commit('setRateCategories', defaultRateCategories)
            commit('setUseThirdAux', res.data.accounting.use_third_aux)
            commit('setAccountingEnabled', res.data.accounting.enabled)
            commit('setSyncProvidersList', res.data.sync_providers)
        },



        /**
         * Generate the auxiliary code for company form
         * @param {*}
         * @returns
         */
         generateAccountingCodes: debounce(({getters, commit})=>{
            if(getters.getUseThirdAux && getters.getType !== "prospect"){
                return new Promise((resolve, reject) => {
                    SellsyJS.WebServiceCaller.get(`/auxiliarycode/getnextcode`, { thirdname: getters.getName, thirdtype: getters.getType }, (res) => {
                        commit('setAuxiliaryCode', res.auxCode)
                            resolve(res);
                        }, err => {
                            reject(err);
                        }
                    )
                })
            }
            return;
        }, 300),
    }
}
